/**
 * Zustand Store
 * Global state management for SPT Driver
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, DeliveryEntry, ClockEntry, TodayClockStatus } from '../types'
import { generateId, isToday, calculateHours } from '../utils/helpers'
import { mockDeliveries } from '../data/mockDeliveries'
import { mockClockEntries } from '../data/mockClockEntries'

// =============================================================================
// STORE TYPES
// =============================================================================

interface AppStore {
  // Auth State
  user: User | null
  isAuthenticated: boolean
  
  // Delivery State
  deliveries: DeliveryEntry[]
  
  // Clock State
  clockEntries: ClockEntry[]
  
  // UI State
  isLoading: boolean
  error: string | null
  
  // Auth Actions
  login: (user: User) => void
  logout: () => void
  
  // Delivery Actions
  addDelivery: (delivery: Omit<DeliveryEntry, 'id' | 'driverId'>) => void
  updateDeliveryStatus: (id: string, status: DeliveryEntry['status']) => void
  deleteDelivery: (id: string) => void
  getTodayDeliveries: () => DeliveryEntry[]
  
  // Clock Actions
  addClockEntry: (entry: Omit<ClockEntry, 'id' | 'driverId'>) => void
  updateClockStatus: (id: string, status: ClockEntry['status']) => void
  getTodayClockStatus: () => TodayClockStatus
  getTodayClockEntries: () => ClockEntry[]
  
  // UI Actions
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
}

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Initial State
      user: null,
      isAuthenticated: false,
      deliveries: mockDeliveries,
      clockEntries: mockClockEntries,
      isLoading: false,
      error: null,
      
      // Auth Actions
      login: (user) => {
        // For driver: update mock data to use the logged-in user's ID
        // For admin: keep original mock data with multiple driver IDs
        if (user.role === 'driver') {
          const updatedDeliveries = mockDeliveries.map(d => ({
            ...d,
            driverId: user.id
          }))
          const updatedClockEntries = mockClockEntries.map(c => ({
            ...c,
            driverId: user.id
          }))
          set({ 
            user, 
            isAuthenticated: true, 
            error: null,
            deliveries: updatedDeliveries,
            clockEntries: updatedClockEntries
          })
        } else {
          // Admin sees all data from multiple drivers
          set({ 
            user, 
            isAuthenticated: true, 
            error: null,
            deliveries: mockDeliveries,
            clockEntries: mockClockEntries
          })
        }
      },
      
      logout: () => set({
        user: null,
        isAuthenticated: false,
        deliveries: [],
        clockEntries: [],
        error: null,
      }),
      
      // Delivery Actions
      addDelivery: (delivery) => {
        const { user } = get()
        if (!user) return
        
        const newDelivery: DeliveryEntry = {
          ...delivery,
          id: generateId(),
          driverId: user.id,
        }
        
        set((state) => ({
          deliveries: [newDelivery, ...state.deliveries],
        }))
      },
      
      updateDeliveryStatus: (id, status) => {
        set((state) => ({
          deliveries: state.deliveries.map((d) =>
            d.id === id ? { ...d, status } : d
          ),
        }))
      },
      
      deleteDelivery: (id) => {
        set((state) => ({
          deliveries: state.deliveries.filter((d) => d.id !== id),
        }))
      },
      
      getTodayDeliveries: () => {
        const { deliveries, user } = get()
        if (!user) return []
        return deliveries
          .filter((d) => d.driverId === user.id && isToday(new Date(d.timestamp)))
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      },
      
      // Clock Actions
      addClockEntry: (entry) => {
        const { user } = get()
        if (!user) return
        
        const newEntry: ClockEntry = {
          ...entry,
          id: generateId(),
          driverId: user.id,
        }
        
        set((state) => ({
          clockEntries: [newEntry, ...state.clockEntries],
        }))
      },
      
      updateClockStatus: (id, status) => {
        set((state) => ({
          clockEntries: state.clockEntries.map((c) =>
            c.id === id ? { ...c, status } : c
          ),
        }))
      },
      
      getTodayClockStatus: () => {
        const { clockEntries, user } = get()
        if (!user) {
          return {
            clockedIn: false,
            clockInTime: null,
            clockOutTime: null,
            totalHours: null,
          }
        }
        
        const todayEntries = clockEntries
          .filter((c) => c.driverId === user.id && isToday(new Date(c.timestamp)))
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        
        // Find the last entry to determine current status
        const lastEntry = todayEntries[todayEntries.length - 1]
        const isCurrentlyIn = lastEntry?.type === 'clock-in'
        
        // Calculate total hours from all clock-in/clock-out pairs
        let totalHours = 0
        let currentClockInTime: Date | null = null
        
        for (let i = 0; i < todayEntries.length; i++) {
          if (todayEntries[i].type === 'clock-in') {
            currentClockInTime = new Date(todayEntries[i].timestamp)
            // If this is the last entry and it's clock-in, this is our current session
            if (i === todayEntries.length - 1) {
              break
            }
          } else if (todayEntries[i].type === 'clock-out' && currentClockInTime) {
            const clockOutTime = new Date(todayEntries[i].timestamp)
            totalHours += calculateHours(currentClockInTime, clockOutTime)
            currentClockInTime = null
          }
        }
        
        // Find first clock-in and last clock-out for display
        const firstClockIn = todayEntries.find((c) => c.type === 'clock-in')
        const lastClockOut = [...todayEntries].reverse().find((c) => c.type === 'clock-out')
        
        return {
          clockedIn: isCurrentlyIn,
          clockInTime: firstClockIn ? new Date(firstClockIn.timestamp) : null,
          clockOutTime: lastClockOut ? new Date(lastClockOut.timestamp) : null,
          totalHours: lastClockOut ? totalHours : null, // Only show total if there was at least one clock-out
        }
      },
      
      getTodayClockEntries: () => {
        const { clockEntries, user } = get()
        if (!user) return []
        return clockEntries
          .filter((c) => c.driverId === user.id && isToday(new Date(c.timestamp)))
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      },
      
      // UI Actions
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
    }),
    {
      name: 'spt-driver-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        deliveries: state.deliveries,
        clockEntries: state.clockEntries,
      }),
    }
  )
)
